import { GroupRoles } from '@bp/pim-auth-constants';
import { ModalBottomButtons } from '@bp/ui-components';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { ProfileSelection } from 'components/ProfileSelection/ProfileSelection';
import { Form, Formik, FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import { showSuccessUpdateToast, showUserDbErrorToast } from 'utils/toasts';
import { uniqueByProfileUuid } from 'utils/uniquByUuid';
import { GroupMember } from '../types/types';
import { UpdateGroupsMutationDocument } from './GroupForm';

export const GroupViewersFormFragment = graphql(`
  fragment GroupViewersFormFields on Query {
    group: groups(where: { uuid: $groupUuid }) {
      __typename
      uuid
      viewers {
        __typename
        uuid
        displayName
        organizationRoles
      }
      editors {
        __typename
        uuid
      }
    }
    ...ProfileSelectionFields
  }
`);

type GroupViewersFormType = {
  viewers: GroupMember[];
};

type GroupViewersFormProps = {
  groupUuid: string;
  schoolYearUuid: string;
  onLoad: (isLoading: boolean) => void;
  onClose: () => void;
  query: FragmentType<typeof GroupViewersFormFragment> | undefined;
};

export const GroupViewersForm = ({ groupUuid, schoolYearUuid, onLoad, onClose, query }: GroupViewersFormProps) => {
  const { t } = useTranslation();

  const fragment = getFragmentData(GroupViewersFormFragment, query);

  const group = fragment?.group[0];

  const [, updateGroup] = useMutation(UpdateGroupsMutationDocument);

  const handleSubmit = async (values: GroupViewersFormType, helpers: FormikHelpers<GroupViewersFormType>) => {
    onLoad(true);

    const { viewers } = values;

    const viewerUuids = uniqueByProfileUuid(viewers).map((v) => v.profileUuid);

    const { error } = await updateGroup({
      update: { viewers: viewerUuids },
      uuid: groupUuid,
    });
    if (error) {
      showUserDbErrorToast();
    } else {
      showSuccessUpdateToast();
    }

    onLoad(false);
    helpers.resetForm();
    onClose();
  };

  const initialValues: GroupViewersFormType = {
    viewers:
      group?.viewers.map((e) => ({
        ...e,
        profileUuid: e.uuid,
        name: e.displayName ?? '',
        groupRole: GroupRoles.Viewer,
      })) ?? [],
  };

  return (
    <Formik<GroupViewersFormType> onSubmit={handleSubmit} initialValues={initialValues}>
      {({ setFieldValue, values }) => (
        <Form>
          <ProfileSelection
            initialMembers={values.viewers.map((m) => ({
              ...m,
              uuid: m.profileUuid,
              selected: false,
            }))}
            excludeMemberUuids={group?.editors.map((e) => e.uuid)}
            onPick={async (picked) => {
              const viewers: GroupMember[] = picked.map((p) => ({
                ...p,
                profileUuid: p.uuid,
                groupRole: GroupRoles.Viewer,
              }));
              await setFieldValue('viewers', viewers);
            }}
            categoriesOnly
            schoolYearUuid={schoolYearUuid}
            query={fragment}
          />

          <ModalBottomButtons
            closeButton={{
              callback: onClose,
            }}
            additionalLeftComponent={t('groups.viewersHint')}
          />
        </Form>
      )}
    </Formik>
  );
};
